/**
 * Which files an upload zone accepts, and why it turns one down.
 *
 * The file picker enforces `accept` on its own, but a file **dropped** on a drop zone carries no
 * such filter: the browser hands over whatever was dragged. Every upload zone therefore runs the
 * same predicate on both paths, and a rejection has to be able to say which file and which rule —
 * "nothing happened" is the worst possible answer to a drop.
 */

/** Just enough of a `File` to be judged — so the rules stay testable without the DOM. */
export interface AcceptableFile {
  name: string;
  /** MIME type as the browser reports it; empty on many systems, hence the extension matching. */
  type?: string;
  /** Size in bytes. */
  size?: number;
}

/** Why a file was turned down, in a sentence meant for the person who dropped it. */
export interface FileRejection {
  fileName: string;
  reason: 'type' | 'size';
  message: string;
}

/**
 * True when the file matches an `accept` list in the HTML sense: a comma-separated mix of
 * extensions (`.pdf`), full MIME types (`application/pdf`) and MIME wildcards (`image/*`).
 *
 * An empty list accepts everything — that is what an `<input type="file">` without `accept` does,
 * and a drop zone must not be stricter than its own picker.
 *
 * Extension matching is on the trailing extension only: `contract.pdf.exe` is an executable, not a
 * contract, and a name that merely contains `.pdf` must not pass. Comparison is case-insensitive —
 * Windows hands over `SCAN.PDF`.
 */
export function isFileAccepted(file: AcceptableFile, accept: string): boolean {
  const tokens = parseAcceptList(accept);
  if (tokens.length === 0) {
    return true;
  }
  const fileName = (file?.name || '').trim().toLowerCase();
  const mimeType = (file?.type || '').trim().toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith('.')) {
      return fileName !== '' && fileName.endsWith(token);
    }
    if (token === '*/*' || token === '*') {
      return true;
    }
    if (token.endsWith('/*')) {
      const family = token.slice(0, -1); // "image/*" -> "image/"
      return mimeType.startsWith(family);
    }
    return mimeType !== '' && mimeType === token;
  });
}

/**
 * The rejection a file earns, or `null` when it may be uploaded.
 *
 * Type is checked before size: telling someone their `.exe` is too large would send them looking
 * for a smaller executable.
 */
export function fileRejection(
  file: AcceptableFile,
  options: { accept?: string; maxSizeMb?: number | null } = {}
): FileRejection | null {
  const fileName = (file?.name || '').trim();
  const accept = options.accept || '';
  const maxSizeMb = options.maxSizeMb ?? null;

  if (fileName === '') {
    return {
      fileName,
      reason: 'type',
      message: 'This file has no name and cannot be uploaded.',
    };
  }

  if (!isFileAccepted(file, accept)) {
    return {
      fileName,
      reason: 'type',
      message: `"${fileName}" is not an accepted file type.`,
    };
  }

  if (maxSizeMb !== null && maxSizeMb > 0) {
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    if ((file?.size || 0) > maxSizeBytes) {
      return {
        fileName,
        reason: 'size',
        message: `"${fileName}" is too large. Max size: ${maxSizeMb}MB.`,
      };
    }
  }

  return null;
}

/**
 * The line printed under the browse button: `Accepted: .pdf, .docx • Max size: 25MB`.
 *
 * Only extensions are listed. A MIME type is how the browser names a format, not how a person does:
 * spelling out `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet` next to `.xlsx`
 * tells the reader nothing they did not already know. An accept list made only of MIME types
 * therefore contributes no format text — the size limit alone is still worth printing.
 */
export function acceptedFormatsHint(
  accept: string,
  maxSizeMb: number | null = null
): string {
  const extensions = parseAcceptList(accept).filter((token) =>
    token.startsWith('.')
  );
  const parts: string[] = [];
  if (extensions.length > 0) {
    parts.push(`Accepted: ${extensions.join(', ')}`);
  }
  if (maxSizeMb !== null && maxSizeMb > 0) {
    parts.push(`Max size: ${maxSizeMb}MB`);
  }
  return parts.join(' • ');
}

/** Splits an `accept` attribute into normalized, non-empty tokens. */
function parseAcceptList(accept: string): string[] {
  return (accept || '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token !== '');
}
